import { ICompetitionApiClient } from '@cyber-range/cyber-range-api-ctf-competition-client';
import { IChallengeApiClient, ChallengePage } from '@cyber-range/cyber-range-api-ctf-challenge-client';
import { ITeamApiClient } from '@cyber-range/cyber-range-api-ctf-team-client';
import { IPlayerApiClient, PlayerPage } from '@cyber-range/cyber-range-api-ctf-player-client';
import { ISubmissionApiClient } from '@cyber-range/cyber-range-api-ctf-submission-client';
import { CompetitionPage } from '@cyber-range/cyber-range-api-ctf-competition-client';
import { ApiPageResponse } from '@cyber-range/cyber-range-api-client';
import {
	fixtureCompetition,
	fixtureChallenges,
	fixtureTeams,
	fixturePlayer,
	fixtureTeamStatistics,
	fixturePlayerStatistics,
	fixtureSubmissions,
	fixtureMyTeamChallengeStatistics,
} from './themeEditorFixtures';

// --- Competition ---

export const MockCompetitionApiClient: Partial<ICompetitionApiClient> =
{
	async get()
	{
		return new CompetitionPage([fixtureCompetition], undefined);
	},
	async getOne()
	{
		return fixtureCompetition;
	},
	async create() { return fixtureCompetition.id; },
	async update() {},
	async delete() {},
	async getCompetitionLimits() { return {} as any; },
	async updateCompetitionLimits() {},
};

// --- Challenge ---

export const MockChallengeApiClient: Partial<IChallengeApiClient> =
{
	async get()
	{
		return new ChallengePage(fixtureChallenges, undefined);
	},
	async getOne(id: string)
	{
		return fixtureChallenges.find(c => c.id === id) || fixtureChallenges[0];
	},
	async add() { return ''; },
	async update() {},
	async delete() {},
	async putChallengeSubmission() { return { isCorrect: false, explanation: '' } as any; },
	async putChallengeHint() { return {} as any; },
	async getChallengeHint() { return {} as any; },
	async updateStatistics() {},
	remapLockedChallengesWithNewIds(items: any[]) { return items; },
};

// --- Team ---

export const MockTeamApiClient: Partial<ITeamApiClient> =
{
	async get()
	{
		return new ApiPageResponse({ items: fixtureTeams });
	},
	async getOne(id: string)
	{
		return fixtureTeams.find(t => t.id === id) || fixtureTeams[0];
	},
	async add() { return ''; },
	async update() {},
	async delete() {},
	async approve() {},
	async join() {},
	async reject() {},
	async kick() {},
	async promote() {},
};

// --- Player ---

export const MockPlayerApiClient: Partial<IPlayerApiClient> =
{
	async get()
	{
		return new PlayerPage([fixturePlayer], undefined);
	},
	async getOne()
	{
		return fixturePlayer;
	},
	async create() { return fixturePlayer.id; },
	async update() {},
	async delete() {},
	async assign() {},
	async unassign() {},
	async startSession() { return {} as any; },
	async renewSession() {},
	async terminateSession() {},
	async getPlayerSession() { return {} as any; },
};

// --- Submission ---

export const MockSubmissionApiClient: Partial<ISubmissionApiClient> =
{
	async get()
	{
		return new ApiPageResponse({ items: fixtureSubmissions });
	},
	async getOne()
	{
		return {} as any;
	},
	async create() { return ''; },
	async delete() {},
	async getCompetitionTeamStatistics()
	{
		return new ApiPageResponse({ items: fixtureTeamStatistics });
	},
	async getCompetitionPlayerStatistics()
	{
		return new ApiPageResponse({ items: fixturePlayerStatistics });
	},
	async getOneCompetitionTeamStatistics(_competitionId: string, teamId: string)
	{
		return fixtureTeamStatistics.find(s => s.teamId === teamId) || fixtureTeamStatistics[0];
	},
	async getOneCompetitionPlayerStatistics()
	{
		return fixturePlayerStatistics[0];
	},
	async getCompetitionTeamPlayerStatistics()
	{
		return new ApiPageResponse({ items: fixturePlayerStatistics });
	},
	async getCompetitionTeamChallengeStatistics()
	{
		return fixtureTeamStatistics[0];
	},
	async listCompetitionTeamChallengesStatistics()
	{
		return new ApiPageResponse({ items: fixtureMyTeamChallengeStatistics });
	},
	async unsolve() {},
	async solve() { return ''; },
};

// --- Empty/No-op clients for non-critical APIs ---

const emptyPageResponse = new ApiPageResponse({ items: [] });

export const MockEmptyApiClient =
{
	async get() { return emptyPageResponse; },
	async getOne() { return {} as any; },
	async getMyAgreements() { return emptyPageResponse; },
	async getKnowledges() { return { knowledges: [], nextPageToken: undefined }; },
	async listAllUsers() { return []; },
	async create() { return ''; },
	async add() { return ''; },
	async update() {},
	async delete() {},
};

export const MockFileApiClient =
{
	async get() { return emptyPageResponse; },
	async getOne() { return {} as any; },
	async getOneDownloadUrl(fileId: string) { return { publicUrl: fileId }; },
	async upload() { return ''; },
	async download() { return '' as any; },
	async delete() {},
	async create() { return ''; },
};

export const MockJobApiClient =
{
	async get() { return emptyPageResponse; },
	async getOne() { return {} as any; },
	async create() { return ''; },
	async delete() {},
};

export const MockSubscriptionApiClient =
{
	async get() { return emptyPageResponse; },
	async getOne() { return {} as any; },
	async create() { return ''; },
	async delete() {},
	async subscribe() {},
	async unsubscribe() {},
	async getOrganizationSubscribedProducts()
	{
		return [
			'feature_allow_public_registration_product_id',
			'feature_allow_public_registration_direct_login_product_id',
			'feature_allow_integrated_environments_product_id',
		];
	},
};
